import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config.js';
import LoginSupportMobile from './LoginSupportMobile';
import './support.css';

export default function LoginSupport() {
  const [contactInfo, setContactInfo] = useState('');
  const [step, setStep] = useState('input_info');
  const [ticketId, setTicketId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ticketStatus, setTicketStatus] = useState('open');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (step !== 'chat' || !ticketId || !contactInfo) return;

    const fetchLatestMessages = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/login-support/${ticketId}/details/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contact_info: contactInfo }),
        });

        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
          setTicketStatus(data.status);
        }
      } catch (err) {
        console.error("خطأ في تحديث المحادثة:", err);
      }
    };

    fetchLatestMessages();
    const intervalId = setInterval(fetchLatestMessages, 4000);
    return () => clearInterval(intervalId);
  }, [step, ticketId, contactInfo]);

  const handleStartChat = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login-support/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_info: contactInfo, message: inputText }),
      });

      const data = await response.json();

      if (response.ok) {
        setTicketId(data.ticket_id);
        setStep('chat');
        setInputText('');
      } else {
        setError(data.error || 'لم يتم العثور على الحساب.');
      }
    } catch (err) {
      setError('خطأ في الاتصال بالخادم.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (inputText.trim() === '' || !ticketId) return;

    const messageText = inputText;
    setInputText('');

    try {
      const res = await fetch(`${API_BASE_URL}/auth/login-support/${ticketId}/reply/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          contact_info: contactInfo, 
          message: messageText 
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        setError(errorData.error || 'فشل إرسال الرد.');
      }
    } catch (err) {
      setError('خطأ في الاتصال بالخادم.');
    }
  };

  // إذا كانت الشاشة موبايل، نعرض واجهة الموبايل المصممة خصيصاً لها
  if (isMobile) {
    return (
      <LoginSupportMobile
        step={step}
        contactInfo={contactInfo}
        setContactInfo={setContactInfo}
        inputText={inputText}
        setInputText={setInputText}
        error={error}
        loading={loading}
        messages={messages}
        ticketStatus={ticketStatus}
        handleStartChat={handleStartChat}
        handleSendMessage={handleSendMessage}
        messagesEndRef={messagesEndRef}
        onBack={() => navigate('/login')}
      />
    );
  }

  // عرض سطح المكتب الاعتيادي
  if (step === 'input_info') {
    return (
      <div className="login-container" dir="ltr">
        <div className="login-left">
          <img className='syria-logo-login-logo' src="/images/Emblem_of_Syria_(2025 present).svg.png" alt="Logo" />
          <h1 className="login-title">الدعم الفني</h1>
          <h4 className="login-sub">محادثة مشكلة تسجيل الدخول</h4>
          
          <form className="login-form" onSubmit={handleStartChat} dir="rtl">
            {error && <div style={{ color: 'red', marginBottom: '15px', textAlign: 'right', fontWeight: 'bold' }}>{error}</div>}

            <div className="input-row">
              <input
                placeholder="أدخل البريد الإلكتروني، اسم المستخدم، أو رقم الهاتف..."
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                type="text"
                required
              />
            </div>
            
            <div className="input-row" style={{ height: 'auto' }}>
              <textarea
                placeholder="اشرح المشكلة لبدء المحادثة مع الإدارة..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                required
                style={{ width: '100%', border: 'none', background: 'transparent', padding: '16px', outline: 'none', resize: 'vertical', minHeight: '100px', fontFamily: 'inherit', fontSize: '16px' }}
              />
            </div>

            <button className="primary" type="submit" disabled={loading}>
              {loading ? 'جاري البدء...' : 'بدء المحادثة'}
            </button>

            <div className="login-links">
              <p>
                <span className="login-link" onClick={() => navigate("/login")}>العودة لتسجيل الدخول</span>
              </p>
            </div>
          </form>
        </div>
        <div className="login-right"></div>
      </div>
    );
  }

  return (
    <div className="support-container" dir="rtl">
      <main className="support-main" style={{ width: '100%', margin: '0' }}>
        <div className="ticket-header" style={{ padding: '15px', background: 'rgba(255,255,255,0.9)', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: '#2c3e50' }}>محادثة الدعم الفني - مشكلة في تسجيل الدخول</h3>
          <button onClick={() => navigate('/login')} style={{ background: '#7f8c8d', color: 'white', padding: '6px 12px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
            خروج
          </button>
        </div>

        <div className="messages-area">
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#7f8c8d' }}>جاري تحميل الرسائل...</div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className={`message-wrapper ${msg.sender === 'user' ? 'message-user' : 'message-support'}`}>
                <div className={`message-bubble ${msg.sender === 'user' ? 'bubble-user' : 'bubble-support'}`}>
                  {msg.text}
                </div>
                <span className={`message-footer ${msg.sender === 'user' ? 'footer-user' : 'footer-support'}`}>
                  {msg.footer}
                </span>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

       <div className="input-area">
          <form onSubmit={handleSendMessage} className="input-form">
            
            {/* استخدام label يغطي المساحة الزرقاء بالكامل مع الذيل المنحني */}
            <label htmlFor="loginSupportInput" className="chat-input-label">
              <input 
                id="loginSupportInput"
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={ticketStatus === 'resolved' || ticketStatus === 'closed' ? "التذكرة مغلقة" : "اكتب ردك هنا..."}
                className="chat-input"
                disabled={ticketStatus === 'resolved' || ticketStatus === 'closed'}
              />
            </label>
            
            {/* زر الإرسال العائم فوق الذيل */}
            <button 
              type="submit" 
              className="send-button"
              disabled={ticketStatus === 'resolved' || ticketStatus === 'closed'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'scaleX(-1)' }}>
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
          
          {(ticketStatus === 'resolved' || ticketStatus === 'closed') && (
            <p style={{ textAlign: 'center', color: '#e74c3c', fontSize: '13px', marginTop: '10px' }}>
              هذه التذكرة مغلقة ولا يمكن الرد عليها.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}